import React from 'react';
import PropTypes from 'prop-types';

//MUI components
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Custom components
import Disclosure from 'components/Disclosure';

// Constants and utils
import FilteredRestrictions from './FilteredRestrictions';

import {
  computeRestrictions,
  computeRestrictionTypeText,
  computeAllowanceTypeText,
  filterRestrictionsOfType
} from './utils';

import {
  RESTRICTION_TYPES,
  ALLOWANCES_TYPES,
  ORGANIZATION_RESTRICTION,
  ORGANIZATION_SEND_VITAL_SIGNS_ALLOWED_PERIOD,
  PATIENT
} from 'constants.js';

const Restrictions = ({resultData, searchPerformedOn}) => {
  const allRestrictions = [
    resultData.dataAccessRestrictions,
    resultData.dataSendAllowances,
    resultData.accessRestrictions,
    resultData.sendAllowances
  ]
    .flat()
    .filter((restriction) => restriction !== undefined);

  const areThereInactiveRestrictions = allRestrictions.filter((restriction) => !restriction.isActive).length > 0;

  const areThereActiveRestrictions = allRestrictions.filter((restriction) => restriction.isActive).length > 0;

  const accessRestrictionsNoFilter =
    searchPerformedOn === PATIENT ? resultData.dataAccessRestrictions : resultData.accessRestrictions;

  const accessRestrictionsWithFilter =
    searchPerformedOn === PATIENT
      ? filterRestrictionsOfType(resultData.dataAccessRestrictions, ORGANIZATION_RESTRICTION)
      : resultData.accessRestrictions;

  const sendAllowancesNoFilter =
    searchPerformedOn === PATIENT ? resultData.dataSendAllowances : resultData.sendAllowances;

  const sendAllowancesWithFilter =
    searchPerformedOn === PATIENT
      ? filterRestrictionsOfType(resultData.dataSendAllowances, ORGANIZATION_SEND_VITAL_SIGNS_ALLOWED_PERIOD)
      : resultData.sendAllowances;

  return (
    <>
      <Disclosure
        isRestricted={resultData.isAccessRestricted}
        text={computeRestrictionTypeText(RESTRICTION_TYPES.accessRestriction, resultData.isAccessRestricted)}
      ></Disclosure>

      <Disclosure
        isRestricted={resultData.isSendRestricted}
        text={computeAllowanceTypeText(ALLOWANCES_TYPES.sendAllowance, !resultData.isSendRestricted)}
      ></Disclosure>
      <Accordion elevation={0} defaultExpanded style={{margin: 0}}>
        <AccordionSummary style={{padding: 0}} expandIcon={<ExpandMoreIcon />}>
          <Typography>Active Restrictions</Typography>
        </AccordionSummary>
        <AccordionDetails style={{display: 'flex', flexDirection: 'column', padding: 0}}>
          {areThereActiveRestrictions ? (
            <>
              <FilteredRestrictions
                restrictions={computeRestrictions(accessRestrictionsNoFilter, true)}
                restrictionType={RESTRICTION_TYPES.accessRestriction}
              />

              <FilteredRestrictions
                restrictions={computeRestrictions(sendAllowancesWithFilter, true)}
                restrictionType={ALLOWANCES_TYPES.sendAllowance}
              />
            </>
          ) : (
            <Typography style={{marginBottom: 24}}>There are no active restrictions</Typography>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion elevation={0} style={{margin: '0 0 16px 0'}}>
        <AccordionSummary style={{padding: 0}} expandIcon={<ExpandMoreIcon />}>
          <Typography>Inactive Restrictions</Typography>
        </AccordionSummary>
        <AccordionDetails style={{display: 'flex', flexDirection: 'column', padding: 0}}>
          {areThereInactiveRestrictions ? (
            <>
              <FilteredRestrictions
                restrictions={computeRestrictions(accessRestrictionsWithFilter, false)}
                restrictionType={RESTRICTION_TYPES.accessRestriction}
              />

              <FilteredRestrictions
                restrictions={
                  !resultData.isSendRestricted
                    ? computeRestrictions(sendAllowancesWithFilter, false)
                    : computeRestrictions(sendAllowancesNoFilter, false)
                }
                restrictionType={ALLOWANCES_TYPES.sendAllowance}
              />
            </>
          ) : (
            <Typography style={{marginBottom: 24}}>There are no inactive restrictions</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

Restrictions.propTypes = {
  resultData: PropTypes.object,
  searchPerformedOn: PropTypes.string
};

export default Restrictions;
